'use client';

import clsx from 'clsx';
import { useState } from 'react';
import { twMerge } from 'tailwind-merge';

const STAR_PATH = 'M8 4L12 4.5L9 7.5L10 12L6 9.5L2 12L3 7.5L0 4.5L4 4L6 0L8 4Z';

export const STAR_FILLED_COLOR = 'text-[#cca300]';
export const STAR_EMPTY_COLOR = 'text-[#DCDCDC]';

const StarIcon = ({ size }: { size: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 12 12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    className="block"
  >
    <path d={STAR_PATH} fill="currentColor" />
  </svg>
);

export interface StarRatingProps {
  value?: number;
  max?: number;
  size?: number;
  className?: string;
  gapClassName?: string;
  starWrapperClassName?: string;
  filledClassName?: string;
  emptyClassName?: string;
  interactive?: boolean;
  onChange?: (value: number) => void;
  ariaLabel?: string;
}

export const StarRating = ({
  value = 0,
  max = 5,
  size = 12,
  className,
  gapClassName = 'gap-1',
  starWrapperClassName,
  filledClassName = STAR_FILLED_COLOR,
  emptyClassName = STAR_EMPTY_COLOR,
  interactive = false,
  onChange,
  ariaLabel
}: StarRatingProps) => {
  const [hoveredValue, setHoveredValue] = useState<number | null>(null);

  const activeValue = interactive ? hoveredValue ?? value : value;
  const safeValue = Math.min(Math.max(activeValue || 0, 0), max);

  const getFillRatio = (index: number) => {
    const fill = safeValue - index;

    if (fill >= 1) return 1;
    if (fill <= 0) return 0;

    return fill;
  };

  const stars = Array.from({ length: max }, (_, index) => {
    const fillRatio = getFillRatio(index);

    const star = (
      <span
        className={twMerge('relative inline-flex', starWrapperClassName)}
        style={{ width: size, height: size }}
      >
        <span className={clsx('absolute inset-0', emptyClassName)}>
          <StarIcon size={size} />
        </span>
        {fillRatio > 0 && (
          <span
            className={clsx(
              'absolute inset-0 overflow-hidden',
              filledClassName
            )}
            style={{ width: `${fillRatio * 100}%` }}
          >
            <span className="block" style={{ width: size, height: size }}>
              <StarIcon size={size} />
            </span>
          </span>
        )}
      </span>
    );

    if (!interactive) {
      return <span className="inline-flex" key={index}>{star}</span>;
    }

    return (
      <button
        key={index}
        type="button"
        className="inline-flex p-0 leading-none"
        onClick={() => onChange?.(index + 1)}
        onMouseEnter={() => setHoveredValue(index + 1)}
        onFocus={() => setHoveredValue(index + 1)}
        onMouseLeave={() => setHoveredValue(null)}
        onBlur={() => setHoveredValue(null)}
        aria-label={`${index + 1}`}
        aria-pressed={value === index + 1}
      >
        {star}
      </button>
    );
  });

  return (
    <div
      className={twMerge('flex items-center', gapClassName, className)}
      role={interactive ? 'group' : 'img'}
      aria-label={ariaLabel ?? `${safeValue} / ${max}`}
    >
      {stars}
    </div>
  );
};

export default StarRating;
